import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import { ArrowRightOutlined, CheckOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatDateEuropean } from '../../../utils/dateUtils';
import { formatUnit } from '../../../utils/units';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import {
  SUPPLIER_ORDER_STAGES,
  buildSupplierOrderDraftChecklist,
  supplierOrderDraftLineLabel,
  type SupplierOrderDraftAction,
  type SupplierOrderDraftFacts,
  type SupplierOrderDraftTask,
} from './supplierOrderDraftStage';
import SupplierOrderStageRail from './SupplierOrderStageRail';
import './SupplierOrderDraftStage.css';

export interface SupplierOrderDraftStageLine {
  name?: string | null;
  quantity?: number | string | null;
  unit?: string | null;
  unitCost?: number | string | null;
  width?: number | string | null;
  height?: number | string | null;
  length?: number | string | null;
  thickness?: number | string | null;
  dimensionDisplayProfile?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
}

interface SupplierOrderDraftStagePanelProps {
  /** Not saved yet (`/app/supplier-orders/new`): choosing the supplier saves it. */
  isNew: boolean;
  supplierName: string | null;
  currency?: string | null;
  lines: SupplierOrderDraftStageLine[];
  pricesVisible: boolean;
  /** Customer order that wrote this confirmation draft (migration 197). */
  sourceOrderId?: string | null;
  expectedDelivery?: string | null;
  canManage: boolean;
  /** „Odrzuć” — a confirmation draft that still waits for review. */
  canReject: boolean;
  /** The status may go to „Wysłane” (backend allowed transitions). */
  canSendStatus: boolean;
  sending?: boolean;
  onChooseSupplier: () => void;
  onOpenLines: () => void;
  onSend: () => void;
  onReject: () => void;
  /** Side card „Dostawa” (trucks, freight, invoice) next to the step card. */
  aside?: React.ReactNode;
  /** „Więcej danych” and any other stage content under the task list. */
  children?: React.ReactNode;
}

/**
 * Stage 1 „Szkic” of the ZD card (makieta „Karta zamówienia ZD”: `renderRail`,
 * `renderStep`, `checklist()`, `primaryKey()`): the five stages, „Etap 1 z 5”,
 * what to do, the task list with each task's own action and one main button of
 * the next step. A new ZD, a saved draft and a confirmation draft all use it.
 */
export default function SupplierOrderDraftStagePanel({
  isNew,
  supplierName,
  currency,
  lines,
  pricesVisible,
  sourceOrderId,
  expectedDelivery,
  canManage,
  canReject,
  canSendStatus,
  sending = false,
  onChooseSupplier,
  onOpenLines,
  onSend,
  onReject,
  aside,
  children,
}: SupplierOrderDraftStagePanelProps) {
  const { t, i18n } = useTranslation(['orders']);
  const facts: SupplierOrderDraftFacts = {
    supplierName,
    lines,
    pricesVisible,
    fromCustomerOrder: Boolean(sourceOrderId),
    expectedDelivery,
  };
  const checklist = buildSupplierOrderDraftChecklist(facts);
  const stageCount = SUPPLIER_ORDER_STAGES.length;
  const namedLines = lines.filter((line) => String(line.name ?? '').trim());
  // Each line: name with its dimensions (same-name lines differ only by size), then the amount.
  const linesSummary = namedLines.map((line) => {
    const quantity = Number(line.quantity);
    const amount = Number.isFinite(quantity) && quantity > 0
      ? ` ${formatLocalizedQuantity(quantity, i18n.language)} ${formatUnit(line.unit || 'pcs', i18n.language)}`
      : '';
    return `${supplierOrderDraftLineLabel(line)}${amount}`;
  }).join(', ');

  const runAction = (action: SupplierOrderDraftAction) => (action === 'chooseSupplier' ? onChooseSupplier() : onOpenLines());
  const actionLabel = (action: SupplierOrderDraftAction, done: boolean) => {
    if (action === 'chooseSupplier') return t(done ? 'orders:supplierOrders.card.changeSupplier' : 'orders:supplierOrders.card.chooseSupplier');
    return t(done ? 'orders:supplierOrders.card.changeLines' : 'orders:supplierOrders.card.lines');
  };

  const taskText = (task: SupplierOrderDraftTask): { text: React.ReactNode; hint?: string } => {
    if (task.key === 'supplier') {
      return supplierName
        ? { text: t('orders:supplierOrders.card.tasks.supplierDone', { supplier: currency ? `${supplierName} (${currency})` : supplierName }) }
        : { text: t('orders:supplierOrders.card.tasks.supplier'), hint: t('orders:supplierOrders.card.tasks.supplierHint') };
    }
    if (task.key === 'lines') {
      const label = pricesVisible ? 'orders:supplierOrders.card.tasks.linesAndPrices' : 'orders:supplierOrders.card.tasks.linesOnly';
      if (!namedLines.length) {
        return { text: t(label), hint: t('orders:supplierOrders.card.tasks.linesHint') };
      }
      const missing = [
        checklist.linesWithoutPrice.length
          ? t('orders:supplierOrders.card.tasks.missingPrice', { names: checklist.linesWithoutPrice.join(', ') })
          : null,
        checklist.linesWithoutQuantity
          ? t('orders:supplierOrders.card.tasks.missingQuantity', { count: checklist.linesWithoutQuantity })
          : null,
      ].filter(Boolean).join('; ');
      return {
        text: `${t(label)}: ${linesSummary}${missing ? ` (${missing})` : ''}`,
        hint: missing ? t('orders:supplierOrders.card.tasks.linesMissingHint') : undefined,
      };
    }
    if (task.key === 'origin') {
      return {
        text: (
          <>
            {t('orders:supplierOrders.card.tasks.origin')}{' '}
            {sourceOrderId ? <Link to={`/app/orders/${sourceOrderId}`}>{t('orders:supplierOrders.card.tasks.originLink')}</Link> : null}
          </>
        ),
      };
    }
    return { text: t('orders:supplierOrders.card.tasks.expectedDelivery', { date: formatDateEuropean(expectedDelivery || '') }) };
  };

  const blockingText = checklist.blocking
    .map((key) => (key === 'supplier' ? t('orders:supplierOrders.card.blocking.supplier') : t('orders:supplierOrders.card.blocking.lines')))
    .join(', ');
  const primary = canManage ? checklist.primaryAction : null;
  const showSend = canManage && (isNew || canSendStatus);
  const sendDisabled = isNew || !checklist.canSend;

  return (
    <section className="zd-stage" data-testid="supplier-order-stage-panel" aria-label={t('orders:supplierOrders.card.ariaLabel')}>
      <SupplierOrderStageRail currentIndex={0} />

      <div className={`zd-stage__grid${aside ? ' has-aside' : ''}`}>
      <div className="zd-stage__body">
        <div className="zd-stage__eyebrow" data-testid="supplier-order-stage-eyebrow">
          {t('orders:supplierOrders.card.stageOf', { current: 1, total: stageCount })}
        </div>
        <h3 className="zd-stage__title">{t('orders:supplierOrders.card.stages.draft')}</h3>
        <p className="zd-stage__help">
          {t(isNew ? 'orders:supplierOrders.card.help.new' : 'orders:supplierOrders.card.help.draft')}
        </p>

        <p className="zd-stage__help" data-testid="supplier-order-origin-hint">
          {t(sourceOrderId ? 'orders:supplierOrders.card.originGenerated' : 'orders:supplierOrders.card.originWarehouse')}
        </p>

        <ul className="zd-stage__checks" data-testid="supplier-order-stage-checklist">
          {checklist.tasks.map((task) => {
            const { text, hint } = taskText(task);
            const state = task.info ? 'info' : task.done ? 'ok' : 'open';
            const showAction = canManage && task.action && task.action !== primary && !task.info;
            return (
              <li key={task.key} className={`zd-stage__check is-${state}`} data-testid={`supplier-order-task-${task.key}`} data-state={state}>
                <span className="zd-stage__check-icon" aria-hidden="true">
                  {task.info ? <InfoCircleOutlined /> : task.done ? <CheckOutlined /> : null}
                </span>
                <span className="zd-stage__check-text">
                  <span>{text}</span>
                  {!task.done && hint ? <small>{hint}</small> : null}
                </span>
                {showAction && task.action ? (
                  <Button size="small" onClick={() => runAction(task.action as SupplierOrderDraftAction)}>
                    {actionLabel(task.action, task.done)}
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>

        {canManage ? (
          <div className="zd-stage__actions">
            {primary ? (
              <Button
                type={showSend ? 'default' : 'primary'}
                onClick={() => runAction(primary)}
                data-testid="supplier-order-stage-primary"
              >
                {actionLabel(primary, false)}
              </Button>
            ) : null}
            {showSend ? (
              <Button
                type="primary"
                disabled={sendDisabled}
                loading={sending}
                onClick={onSend}
                icon={<ArrowRightOutlined />}
                iconPosition="end"
                data-testid="supplier-order-stage-send"
              >
                {t('orders:supplierOrders.drafts.sendToSupplier')}
              </Button>
            ) : null}
            {canReject ? (
              <Button danger type="text" onClick={onReject} data-testid="supplier-order-stage-reject">
                {t('orders:supplierOrders.card.reject')}
              </Button>
            ) : null}
            {showSend && sendDisabled ? (
              <span className="zd-stage__blocked" data-testid="supplier-order-stage-send-blocked">
                {isNew && !checklist.blocking.length
                  ? t('orders:supplierOrders.card.blocking.newNotSaved')
                  : t('orders:supplierOrders.card.blocking.prefix', { tasks: blockingText })}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      {aside ? <aside className="zd-stage__aside" data-testid="supplier-order-stage-aside">{aside}</aside> : null}
      </div>

      {children ? <div className="zd-stage__extra">{children}</div> : null}
    </section>
  );
}
